import { Effect, Option, Schema as S } from "effect";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";

import type { Page, PageReturn } from "../shared";
import { playThaxted } from "./audio";
import { Message } from "./message";
import { HISTORY_CAP, type Model, SESSION_LIMIT_MS } from "./model";
import { sendToolResult } from "./session";
import { executeTool } from "./tools";

// COMMANDS

export const PlayJingle = Command.define("PlayAiJingle", {
	messages: [Message.CompletedJingle],
	execute: Effect.sync(playThaxted).pipe(
		Effect.as(Message.CompletedJingle()),
		Effect.catch(() => Effect.succeed(Message.CompletedJingle())),
	),
});

/** Runs one function call the model asked for; the outcome becomes a history entry either way. */
export const RunTool = Command.define("RunAiTool", {
	args: { id: S.String, callId: S.String, name: S.String, args: S.String },
	messages: [Message.CompletedTool],
	execute: ({ id, callId, name, args }) =>
		Effect.gen(function* () {
			const parsed = yield* Effect.try({
				try: () => JSON.parse(args) as Record<string, unknown>,
				catch: () => new Error("Invalid function arguments"),
			});
			const result = yield* executeTool(name, parsed);
			return Message.CompletedTool({
				id,
				callId,
				name,
				success: true,
				details: JSON.stringify(parsed),
				output: JSON.stringify(result),
				timestamp: new Date().toISOString(),
			});
		}).pipe(
			Effect.catch((error) =>
				Effect.succeed(
					Message.CompletedTool({
						id,
						callId,
						name,
						success: false,
						details: error.message,
						output: "",
						timestamp: new Date().toISOString(),
					}),
				),
			),
		),
});

export const SendToolResult = Command.define("SendAiToolResult", {
	args: { id: S.String, callId: S.String, output: S.String },
	messages: [Message.CompletedSendToolResult],
	execute: ({ id, callId, output }) =>
		sendToolResult(id, callId, output).pipe(
			Effect.as(Message.CompletedSendToolResult()),
		),
});

// UPDATE

type Return = PageReturn<Model, Message>;

const inSession = (model: Model, id: string): boolean =>
	Option.exists(model.session, (session) => session.id === id);

const disconnected = (model: Model): Model =>
	evo(model, {
		session: () => Option.none(),
		remaining: () => SESSION_LIMIT_MS / 1000,
	});

export const initial: Model = {
	page: "AI",
	session: Option.none(),
	remaining: SESSION_LIMIT_MS / 1000,
	error: Option.none(),
	history: [],
	seq: 0,
};

export const page: Page<Model, Message> = {
	init: () => ({ model: initial }),
	changedRoute: (model) => ({ model }),
	update: (model, message) =>
		Message.match<Return>(message, {
			ClickedToggle: () =>
				Option.isSome(model.session)
					? { model: disconnected(model) }
					: {
							model: evo(model, {
								session: () =>
									Option.some({
										id: `s-${model.seq}`,
										startedAt: Option.none(),
									}),
								error: () => Option.none(),
								seq: (seq) => seq + 1,
							}),
							commands: [PlayJingle()],
						},
			CompletedJingle: () => ({ model }),
			SessionConnected: ({ id, at }) => ({
				model: inSession(model, id)
					? evo(model, {
							session: () => Option.some({ id, startedAt: Option.some(at) }),
							remaining: () => SESSION_LIMIT_MS / 1000,
						})
					: model,
			}),
			SessionFailed: ({ id, error }) => ({
				model: inSession(model, id)
					? evo(disconnected(model), { error: () => Option.some(error) })
					: model,
			}),
			ToolCalled: ({ id, callId, name, args }) =>
				inSession(model, id)
					? { model, commands: [RunTool({ id, callId, name, args })] }
					: { model },
			CompletedTool: ({
				id,
				callId,
				name,
				success,
				details,
				output,
				timestamp,
			}) => ({
				model: evo(model, {
					history: (history) =>
						[
							{ id: callId, name, timestamp, success, details },
							...history,
						].slice(0, HISTORY_CAP),
				}),
				// v1 only answers successful calls; a failure is recorded and the model is never told.
				commands:
					success && inSession(model, id)
						? [SendToolResult({ id, callId, output })]
						: [],
			}),
			CompletedSendToolResult: () => ({ model }),
			Ticked: ({ now }) =>
				Option.match(model.session, {
					onNone: () => ({ model }),
					onSome: ({ startedAt }) =>
						Option.match(startedAt, {
							onNone: () => ({ model }),
							onSome: (started) => {
								const remaining = Math.max(
									0,
									Math.floor((SESSION_LIMIT_MS - (now - started)) / 1000),
								);
								return {
									model:
										remaining <= 0
											? disconnected(model)
											: evo(model, { remaining: () => remaining }),
								};
							},
						}),
				}),
		}),
};

// SUBSCRIPTION SELECTORS (lifted to the root in `app/update.ts`)

/** `Some(id)` while a session is requested or active: the WebRTC stream's gate. */
export const sessionDependencies = (model: Model) => ({
	maybeSessionId: Option.map(model.session, (session) => session.id),
});

/** `Some` only once connected: the one-second countdown's gate. */
export const countdownDependencies = (model: Model) => ({
	maybeStarted: Option.flatMap(model.session, (session) =>
		Option.map(session.startedAt, (startedAt) => ({
			id: session.id,
			startedAt,
		})),
	),
});
